import {vec3, mat4, quat, FrameBuffer, Scene, Container, Mesh, HDRTexture, Material, SphereGeometryBuffer,  Program,Camera, Texture, PlaneGeometryBuffer} from '../beam/index';
import settings, {debugData} from "./settings";
import DenoiserRenderPass from './render-pass/DenoiserRenderPass';
import SSRRenderPass from './render-pass/SSRRenderPass';
import CopyRenderPass from './render-pass/CopyRenderPass';

import buildMesh, { clearCache } from './loaders/buildMesh';
import HDRSky from './render-pass/HDRSky'
import BloomRenderPass from './render-pass/BloomRenderPass';
import CloudsRenderPass from './render-pass/CloudsRenderPass';
import ToneMappingRenderPass from './render-pass/ToneMappingRenderPass';
import SMAARenderPass from './render-pass/SMAARenderPass';

import VirtualTexture, {clearTileIndex} from '../beam/VirtualTexture';

import LTC1 from './ltc1.js'
import LTC2 from './ltc2.js'

import CopyVS from './shaders/copy-vs.glsl';
import CopyFS from './shaders/copy-fs.glsl';
import AlphaComposeVS from './shaders/alpha-compose-vs.glsl';
import AlphaComposeFS from './shaders/alpha-compose-fs.glsl';
import FinalVS from './shaders/final-vs.glsl';
import FinalFS from './shaders/final-fs.glsl';
import ConvolutionVS from './shaders/convolution-vs.glsl';
import ConvolutionFS from './shaders/convolution-fs.glsl';
import worleyVS from './shaders/worley-vs.glsl';
import worleyFS from './shaders/worley-fs.glsl';



function nearestOf2(n) {
    if(n > 0)
        return Math.ceil(n/2) * 2;
    else if( n < 0)
        return Math.floor(n/2) * 2;
    else
        return 2
}

function nestedEach(obj, callback, lvl) {
    if (!obj) {
        return
    }
    lvl = lvl || 0
    callback( obj, lvl )
    obj.children && obj.children.forEach((child)=>{
        nestedEach( child, callback, lvl+1 )
    });
}
function distance (ro, rd, aabbMin, aabbMax) {
    let t = -1;
    // rd is unit direction vector of ray
    let dirfrac = vec3.create()
    dirfrac[0] = 1.0 / rd[0];
    dirfrac[1] = 1.0 / rd[1];
    dirfrac[2] = 1.0 / rd[2];
    let lb = aabbMin
    let rt = aabbMax
    // lb is the corner of AABB with minimal coordinates - left bottom, rt is maximal corner
    // ro is origin of ray
    let t1 = (lb[0] - ro[0])*dirfrac[0];
    let t2 = (rt[0] - ro[0])*dirfrac[0];
    let t3 = (lb[1] - ro[1])*dirfrac[1];
    let t4 = (rt[1] - ro[1])*dirfrac[1];
    let t5 = (lb[2] - ro[2])*dirfrac[2];
    let t6 = (rt[2] - ro[2])*dirfrac[2];
    let tmin = Math.max(Math.max(Math.min(t1, t2), Math.min(t3, t4)), Math.min(t5, t6));
    let tmax = Math.min(Math.min(Math.max(t1, t2), Math.max(t3, t4)), Math.max(t5, t6));
    // if tmax < 0, ray (line) is intersecting AABB, but the whole AABB is behind us
    if (tmax < 0)
    {
        t = tmax;
        return -1;
    }
    // if tmin > tmax, ray doesn't intersect AABB
    if (tmin > tmax)
    {
        t = tmax;
        return -1;
    }
    t = tmin;
    return t;
}

function boxIntersection ( ro, rd, aabbmin, aabbmax) {
    let out
    var d = distance(ro, rd, aabbmin, aabbmax)
    if (d === -1) {
        out = null
    } else {
        out = vec3.create();
        out[0] = ro[0] + rd[0] * d
        out[1] = ro[1] + rd[1] * d
        out[2] = ro[2] + rd[2] * d
    }
    return out
}


export default class RenderPipeline {

    constructor(options) 
    {

        this._oldCameraDirection = this._oldCameraDirection || vec3.create()

        this.HDRTexturesCache      = {}
        this.HDRTexture            = null

        this.time                  = 0
        this.onMouseMove           = this.onMouseMove.bind(this)
        this.onMouseDown           = this.onMouseDown.bind(this)
        this.onMouseUp             = this.onMouseUp.bind(this)
        this.rayDirection          = vec3.create()
        this.rayOrigin             = vec3.create()
        this.clearColor            =  [1,1,1]
        this.cameraTarget          = [0,0,0]
        this.jitterIndex           = 0;
        this.poisonIndex           = 0
        this.currentLightIndex     = 0;
        this.shadowMapUpdateRounds = 0
        this.isHDRLoaded         = false;//flag for convolution pass
        this.IBLFrameCount       = 0//49;
        this._uniforms           = {}
        this._defines            = {}

        this.options             = options;
        this.lightSize           = options.lightSize || settings.uLightSize

        this.beforeShadowPass    = options.beforeShadowPass || function() {}
        this.beforeDepthPass     = options.beforeDepthPass || function() {}
        this.beforeMainPass      = options.beforeMainPass || function() {}
        this.afterMainPass       = options.afterMainPass || function() {}
        this.onRenderPass        = options.onRenderPass || function(){}
        
        this.resolution          = options.resolution || [widnow.innerWidth, 1];
        this.scene               = options.scene;
        this.renderer            = options.renderer
        this.camera              = options.camera;


        this.blankFrameBuffer = new FrameBuffer(this.renderer.gl, {
            width:  1,
            height: 1,
        });

        this.debugFrameBuffer = new FrameBuffer(this.renderer.gl, {
            width:  1,
            height: 1,
            type:   this.renderer.gl.FLOAT
        });


        this.reflectionProbeTexture = Texture.fromUrl(this.renderer.gl,this.options.lobby_reflection_map, {
            linear: true,
            mipmap: false,
        })
        this.reflectionProbeFrameBuffer = new FrameBuffer(this.renderer.gl, {
			width:  settings.reflectionProbeResolution,
			height: settings.reflectionProbeResolution,
			linear: false,
			type: this.renderer.gl.FLOAT
        });

        if (settings.useVirtualTexture) {
            this.virtualTextureFrameBuffer = new FrameBuffer(this.renderer.gl, {
                width:  settings.virtualTextureResolution,
                height: settings.virtualTextureResolution,
                linear: false,
                type: this.renderer.gl.FLOAT
            });
        }

        this.alphaFrameBuffer = new FrameBuffer(this.renderer.gl, {
            width:  1,
            height: 1,
            type:   this.renderer.gl.FLOAT
        });
        
        // this.initPointer()
        this.createBufferScene()
        this.createCameraMatrices()
        this.createBlueNoise()
        
        this.createLTC_LUT()
        this.prepareGPass()
        this.createShadowBuffers()
        this.prepareFinalPass()

        this.buildEnvironment()
        this.buildSkySphere()
        this.buildWorleyNoise()

        

        this.smaaRenderPass          = new SMAARenderPass(this.renderer);
        this.SSRRenderPass           = new SSRRenderPass(this.renderer);
        this.CopyRenderPass          = new CopyRenderPass(this.renderer)
        // this.dofRenderPass           = new DOFRenderPass( this.renderer )
        this.bloomRenderPass         = new BloomRenderPass(this.renderer)
        this.temporalAARenderPass    = new DenoiserRenderPass( this.renderer, { 
            useNeighbourgClipping:   true, 
            useTemporalUpscale:      true,
            bilateralBlurIterations: 0,
            spatialThreshold:        1,
            temporalThreshold:       0.9, 
            useAtrouNoiseFilter:     false, 
            useVariance:             false, 
            useLongHistory:          false, 
            useDepthCheck:           false,
        })
        this.hdrSky                  = new HDRSky(this.renderer)
        this.cloudsPass              = new CloudsRenderPass(this.renderer);
        // this.chromaticAberrationPass = new ChromaticAberrationRenderPass(this.renderer);
        this.toneMappingRenderPass   = new ToneMappingRenderPass(this.renderer);
    }

    clearCache() {
        clearCache()
    }

    destroy() {
        
    }

    buildSkySphere() {
        this.skySphere = new Mesh();
        this.skySphere.backSided = true;
        this.skySphere.geometry = new SphereGeometryBuffer(this.renderer.gl, {
            radius:        1,
            heightSegments: 20,
            widthSegments:  20,
        });
        let skyMaterial = buildMesh(this.renderer.gl, { 
            albedo:        [177./255., 185./255., 198./255.],
            emissiveColor: [177./255., 185./255., 198./255.],
            emissiveIntensity: 1,
        });
        this.skySphere.material = skyMaterial.material
        this.skySphere.uniforms = skyMaterial._uniforms;
        this.skySphere.static = false;
    }

    //connect uniforms and draw shadow map atlas based on current scene lights
    useScene(scene, skipInit, cb) {


        this.scenereadyCallback = cb;
        this.scene              = scene;
        
        scene.uniforms = Object.assign(this._uniforms,scene.uniforms)
        scene.defines  = Object.assign(this._defines,scene.defines)
        
        if (!skipInit) { //skip init when switching modes as the scene is the same
            if (this.scene && this.skySphere) {
                this.scene.add(this.skySphere);
            }
        }
        
        //as change area/spotlight type based on profile, we need to update light uniforms on mode/scene chnage
        this.lightsNeedUpdate = true;

        //prevent white flash when TAA kicks in
        settings.uClear = true;
        this.numRenderedFrames     = 0;
        
        //only update shadowmap on first scene load, not when switching scene
        if (!skipInit) {
            // if (this._useSky) {
            //     this.IBLFrameCount         = 0;
            // }
            settings.updateShadows     = true;
            this.currentLightIndex     = 0
            this.shadowMapUpdateRounds = 0
        }

    }

    useCamera(camera) {
        this.camera = camera
        this.hasCameraMoved = true
        settings.useNeighbourgClipping = true
        this.resetCameraStatus()
    }

    initPointer() {
        this.pointer = [0,0]
        this.isPointerDown = false
        document.addEventListener('mousedown', this.onMouseDown, false)
        document.addEventListener('mousemove', this.onMouseMove, false)
        document.addEventListener('mouseup', this.onMouseUp, false)

        this.pointerHelper = new Mesh();
        this.pointerHelper.name = 'Pointer Helper'
        this.pointerHelper.id = 'Pointer Helper'
        this.pointerHelper.geometry = new PlaneGeometryBuffer(this.renderer.gl, {
            width:         100,
            height:        100,
            heightSegments: 1,
            widthSegments:  1,
        });
        this.pointerHelper.rotation[0] = Math.PI/2
        this.pointerHelper.material = new Material(this.renderer.gl,{color: [0,1,0], wireframe: true})
        this.pointerHelper.isHelper = true
        
    }
    onMouseDown( event ) {
        this.isPointerDown = true;
    }
    onMouseUp( event ) {
        this.isPointerDown = false;
        this.hasMoved = false
    }
    onMouseMove( event ) {
        // calculate mouse position in normalized device coordinates
        // (-1 to +1) for both components
        this.pointer[0] =   ( (event.clientX) / window.innerWidth  ) * 2 - 1;
        this.pointer[1] = - ( (event.clientY) / window.innerHeight ) * 2 + 1;
    }
    // updatePointerRay() {
    //     if (!this.scene) {
    //         return
    //     }
    //     this.rayOrigin    = vec3.set( this.rayOrigin, this.camera.worldMatrix[12], this.camera.worldMatrix[13], this.camera.worldMatrix[14] )
    //     this.rayDirection = this.cameraDirection//vec3.set( this.rayDirection, this.pointer[0], this.pointer[1], 0.5 )
    //     // vec3.transformMat4( this.rayDirection, this.rayDirection, this.camera.inverseProjectionMatrix )
    //     // vec3.transformMat4( this.rayDirection, this.rayDirection, this.camera.worldMatrix );
    //     // vec3.subtract( this.rayDirection, this.rayDirection, this.rayOrigin )
    //     // vec3.normalize(this.rayDirection, this.rayDirection);
        
    //     let mouseHelperPos = vec3.create();
    //     let sDir = vec3.create()
    //     vec3.scale(sDir, this.rayDirection, 15) 
    //     vec3.add( mouseHelperPos, this.rayOrigin, sDir )
    //     vec3.copy( this.pointerHelper.position, mouseHelperPos);


    //     this.hoverObject = null;
    //     let lastHitLevel    = 0;
    //     let lastHitDistance = 10000000;
    //     let tmp = vec3.create();
    //     // nestedEach(this.scene, (obj, level)=>{
    //     //     if (obj.boudingBox && !obj.isHelper) {
    //     //         let minAABB = vec3.fromValues( obj.boudingBox.min[0], obj.boudingBox.min[1], obj.boudingBox.min[2] )
    //     //         let maxAABB = vec3.fromValues( obj.boudingBox.max[0], obj.boudingBox.max[1], obj.boudingBox.max[2] )
    //     //         vec3.transformMat4( minAABB, minAABB, obj.worldMatrix );
    //     //         vec3.transformMat4( maxAABB, maxAABB, obj.worldMatrix );
    //     //         let hitPosition = boxIntersection( 
    //     //             this.rayOrigin, 
    //     //             this.rayDirection, 
    //     //             [
    //     //                 [minAABB[0], minAABB[1], minAABB[2]],  
    //     //                 [maxAABB[0], maxAABB[1], maxAABB[2]]
    //     //             ]
    //     //         )
    //     //         if (hitPosition ) {
    //     //             vec3.copy( this.pointerHelper.position, hitPosition);
    //     //             vec3.subtract(tmp, this.rayOrigin, hitPosition);
    //     //             let dist = vec3.length(tmp);
    //     //             if (dist <= lastHitDistance || level>lastHitLevel) {
    //     //                 lastHitDistance = dist
    //     //                 lastHitLevel = level;
    //     //                 this.hoverObject = obj
    //     //             }
    //     //         }
    //     //     }
    //     // });

    //     let hitPosition = boxIntersection( 
    //         this.rayOrigin, 
    //         this.rayDirection, 
    //         [ -8000,200, -8000],  
    //         [ 8000, 200,  8000]
    //     )
    //     if (hitPosition ) {
    //         vec3.copy( this.pointerHelper.position, hitPosition);
    //         vec3.subtract(tmp, this.rayOrigin, hitPosition);
    //         if (this.isPointerDown && !this.hasMoved) {
    //             this.hasMoved = true
    //             this.cameraTarget[0] = hitPosition[0]
    //             this.cameraTarget[2] = hitPosition[2]
    //         }
    //     }
    // }

    createLTC_LUT() {
        this.ltc_1 = new Texture(this.renderer.gl,{
            linear: true,
            mipmap: false,
            width: 64, height: 64,
            type: this.renderer.gl.FLOAT,
            data: new Float32Array(LTC1)
        })
        this.ltc_2 = new Texture(this.renderer.gl,{
            linear: true,
            mipmap: false,
            width: 64, height: 64,
            type: this.renderer.gl.FLOAT,
            data: new Float32Array(LTC2)
        })
    }

    createBlueNoise() {
        this.frameIndex = 0;
        this.bndsTextures = []
        this.bndsTextureIndex = 0;
        for (let i=0; i<1; i++) {
            this.bndsTextures[i] = Texture.fromUrl(this.renderer.gl,this.options.noise, {
                linear: false,
                mipmap: false,
            })
        }
    }


    prepareGPass() {
		this.depthFrameBufferFront = new FrameBuffer(this.renderer.gl, {
            width:  1,
            height: 1,
            linear: false,
            type:   this.renderer.gl.FLOAT
        });
        this.depthFrameBufferBack = new FrameBuffer(this.renderer.gl, {
            width:  1,
            height: 1,
            linear: false,
            type:   this.renderer.gl.FLOAT
        });
        this.writeGFrameBuffer = this.depthFrameBufferFront;
        this.readGFrameBuffer  = this.depthFrameBufferBack;
        this.lastDepthFrameBuffer = new FrameBuffer(this.renderer.gl, {
            width:  1,
            height: 1,
            linear: false,
            type:   this.renderer.gl.FLOAT
        });
    }


    prepareFinalPass() {


        this.copyMaterial = new Program(this.renderer.gl,{
			vertexShader:   CopyVS,
			fragmentShader: CopyFS,
			depthTest: false,
			blend:     false,
        });
        this.copyMaterial.name = 'copyMaterial'

        this.alphaComposeMaterial = new Program(this.renderer.gl,{
			vertexShader:   AlphaComposeVS,
			fragmentShader: AlphaComposeFS,
			depthTest: false,
			blend:     false,
        });
        this.alphaComposeMaterial.name = 'alphaComposeMaterial'

        this.finalMaterial = new Program(this.renderer.gl,{
			vertexShader:   FinalVS,
			fragmentShader: FinalFS,
			depthTest: false,
            blend:     false,
            name: "finalMaterial"
        });
        this.finalMaterial.name = 'finalMaterial'

        this.frontFrameBuffer = new FrameBuffer(this.renderer.gl, {
            width:  1,//this.resolution[0],
            height: 1,//this.resolution[1],
            linear: true,
            type: settings.deferedRendering ? this.renderer.gl.FLOAT : 'HALF_FLOAT'//this.renderer.gl.UNSIGNED_BYTE
        });
        this.backFrameBuffer = new FrameBuffer(this.renderer.gl, {
            width:  1,//this.resolution[0],
            height: 1,//this.resolution[1],
            linear: true,
            type: settings.deferedRendering ? this.renderer.gl.FLOAT : 'HALF_FLOAT'//this.renderer.gl.UNSIGNED_BYTE
        });
        this.writeBuffer = this.frontFrameBuffer;
        this.readBuffer  = this.backFrameBuffer;

        this.frontHDFrameBuffer = new FrameBuffer(this.renderer.gl, {
            width:  1,//this.resolution[0],
            height: 1,//this.resolution[1],
            linear: true,
            type: settings.deferedRendering ? this.renderer.gl.FLOAT :  'HALF_FLOAT'//this.renderer.gl.UNSIGNED_BYTE
        });
        this.backHDFrameBuffer = new FrameBuffer(this.renderer.gl, {
            width:  1,//this.resolution[0],
            height: 1,//this.resolution[1],
            linear: true,
            type: settings.deferedRendering ? this.renderer.gl.FLOAT :  'HALF_FLOAT'//this.renderer.gl.UNSIGNED_BYTE
        });
        this.writeHDBuffer = this.frontHDFrameBuffer;
        this.readHDBuffer  = this.backHDFrameBuffer;
    }

    buildEnvironment() {



        this.convoltionMaterial    = new Program(this.renderer.gl, {
			vertexShader:   ConvolutionVS,
			fragmentShader: ConvolutionFS
        })
        this.convoltionMaterial.name = 'convoltionMaterial'
        //specular IBL
        for (let i=0; i<5; i++) {
            this['specularIBLFrameBuffer'+i] = new FrameBuffer(this.renderer.gl, {
                width:  i == 0 ? 2048 : settings.IBLResolution[0],
                height: i == 0 ? 1024 : settings.IBLResolution[1],
                linear: true,
                type: this.renderer.gl.FLOAT
            });
            if (i>0) {
                this['specularIBLDenoiserPass'+i] = new DenoiserRenderPass( this.renderer, {bilateralBlurIterations: 0, useReprojection: false})
                this['denoisedSpecularIBLFrameBuffer'+i] = new FrameBuffer(this.renderer.gl, {
                    width:  settings.IBLResolution[0],
                    height: settings.IBLResolution[1],
                    linear: true,
                    type: this.renderer.gl.FLOAT
                });
                this['specularIBLDenoiserPass'+i].resize( settings.IBLResolution[0], settings.IBLResolution[1] )
            }
        }
        this.diffuseIBLFrameBuffer = new FrameBuffer(this.renderer.gl, {
            width:  settings.IBLResolution[0],
            height: settings.IBLResolution[1],
            linear: true,
            type: this.renderer.gl.FLOAT
        });
        this.denoisedDiffuseIBLFrameBuffer = new FrameBuffer(this.renderer.gl, {
            width:  settings.IBLResolution[0],
            height: settings.IBLResolution[1],
            linear: true,
            type: this.renderer.gl.FLOAT
        });
        this.diffuseIBLDenoiserPass  = new DenoiserRenderPass( this.renderer, {bilateralBlurIterations: 5, useReprojection: false})
        this.diffuseIBLDenoiserPass.resize( settings.IBLResolution[0], settings.IBLResolution[1] )




        //USED TO CHECK 1 unit scale and radiance
        //TODO: use it as irrandiance probes ?...
        // this.refSphere = new Container();
        // this.refSphere.id = 'scale Ref'
        // this.refSphere.name = 'scale Ref'
        // let refMaterial = buildMesh(this.renderer.gl, { 
        //     color:        [1, 1, 1],
        //     roughness: 1,

        // });
        // let geometry = new SphereGeometryBuffer(this.renderer.gl, {
        //     radius:         100,//100cm
        //     heightSegments: 20,
        //     widthSegments:  20,
        // });
        // for (let i=0; i<10; i++) {
        //     for (let j=0; j<10; j++) {
        //         for (let k=0; k<4; k++) {
        //             let mesh = new Mesh();
        //             mesh.geometry = geometry;
        //             mesh.position[0] = 1000 * (i-4);
        //             mesh.position[1] = 50 + 1000 * k;
        //             mesh.position[2] = 3000 * (j-4);
        //             mesh.material = refMaterial.material
        //             mesh.uniforms = refMaterial._uniforms;
        //             this.refSphere.add( mesh )
        //         }
        //     }
        // }




    }


    buildWorleyNoise() {



        this.worleyMaterial    = new Program(this.renderer.gl, {
			vertexShader:   worleyVS,
			fragmentShader: worleyFS,
		})
        this.worleyFrameBuffer0 = new FrameBuffer(this.renderer.gl, {
            width:  512,
            height: 512,
            wrapS: this.renderer.gl.REPEAT,
            wrapT: this.renderer.gl.REPEAT,
            linear: true,
        });
        this.worleyFrameBuffer1 = new FrameBuffer(this.renderer.gl, {
            width:  512,
            height: 512,
            wrapS: this.renderer.gl.REPEAT,
            wrapT: this.renderer.gl.REPEAT,
            linear: true,
        });
        this.worleyFrameBuffer2 = new FrameBuffer(this.renderer.gl, {
            width:  512,
            height: 512,
            wrapS: this.renderer.gl.REPEAT,
            wrapT: this.renderer.gl.REPEAT,
            linear: true,
        });
    }

    createBufferScene() 
    {
        this.bufferCamera = new Camera({
            left:          -this.resolution[0]/2,
            right:         this.resolution[0]/2,
            top:           this.resolution[1]/2,
            bottom:        -this.resolution[1]/2,
            near:          1,
            far:           4000.0,
            type:          'ortho',
            orbitControl:  false,
            lookAt:        [0,0,0] 
        });
        this.bufferCamera.position[2] = 1000;
        this.bufferCamera.position[0] = 0.01;
        this.bufferCamera.position[1] = 0.01;
        this.bufferCamera.lookAt = vec3.create()

        this.bufferScene  = new Scene();
        this.bufferScene.uniforms = Object.assign(this._uniforms,this.bufferScene.uniforms)

        this.bufferPlane = new Mesh();
        this.bufferPlane.name = 'Beam Engine Buffer Plane'
        // this.bufferPlane.static = true
        this.bufferPlane.geometry = new PlaneGeometryBuffer(this.renderer.gl, {width:  1, height: 1});
        this.bufferPlane.scale[0] = this.resolution[0]
        this.bufferPlane.scale[1] = this.resolution[1]
        this.bufferScene.add(this.bufferPlane)
    }

    createCameraMatrices()
    {
        this.inverseViewProjectionMatrix = mat4.create()
        this.lightInvVPMatrix            = mat4.create()
		this.viewProjectionwMatrix        = mat4.create()
		this.cameraDirection             = vec3.create()
        this.cameraPosition              = vec3.create()

        this.lastViewProjectionMatrix    = mat4.create()
        this.lastInvViewProjectionMatrix = mat4.create()
    }
    
    createShadowBuffers()
    {
        this.shadowMap0 = new FrameBuffer(this.renderer.gl, {
			width:  settings.uShadowMapResolution,
			height: settings.uShadowMapResolution,
			linear: false,
			type: this.renderer.gl.FLOAT
        });
        this.shadowMap1 = new FrameBuffer(this.renderer.gl, {
			width:  settings.uShadowMapResolution,
			height: settings.uShadowMapResolution,
			linear: false,
			type: this.renderer.gl.FLOAT
        });
        this.shadowMap2 = new FrameBuffer(this.renderer.gl, {
			width:  settings.uShadowMapResolution,
			height: settings.uShadowMapResolution,
			linear: false,
			type: this.renderer.gl.FLOAT
        });
        this.shadowMap3 = new FrameBuffer(this.renderer.gl, {
			width:  settings.uShadowMapResolution,
			height: settings.uShadowMapResolution,
			linear: false,
			type: this.renderer.gl.FLOAT
        });
        this.shadowMapCenter = new FrameBuffer(this.renderer.gl, {
			width:  settings.uShadowMapResolution,
			height: settings.uShadowMapResolution,
			linear: false,
			type: this.renderer.gl.FLOAT
        });

        // console.log('CREATE_SHADOW_BUFFERS', settings.uShadowMapResolution);

        this.shadowMap0.skipViewport = true
        this.shadowMap1.skipViewport = true
        this.shadowMap2.skipViewport = true
        this.shadowMap3.skipViewport = true
        this.shadowMapCenter.skipViewport = true
        // this.shadowFrameBuffer = new FrameBuffer(this.renderer.gl, {
        //     width:   this.resolution[0],
        //     height:  this.resolution[1],
        //     linear: true,
        // });
        // this.denoisedShadowFrameBuffer = new FrameBuffer(this.renderer.gl, {
        //     width:   this.resolution[0],
        //     height:  this.resolution[1],
        //     linear: true,
        // });
    }


    updateDefines() {
        if (settings.useAreaLights) this._defines.USE_AREA_LIGHTS = 1
        else delete this._defines.USE_AREA_LIGHTS

        if (settings.useSoftShadows /*&& this.useSoftShadows*/) this._defines.USE_PCSS = 1
        else delete this._defines.USE_PCSS
        
        if (settings.useIBL) this._defines.USE_IBL = 1
        else delete this._defines.USE_IBL

        if (settings.useReflectionProbe) this._defines.USE_REFLECTION_PROBE = 1
        else delete this._defines.USE_REFLECTION_PROBE

        if (settings.useFog) this._defines.USE_FOG = 1
        else delete this._defines.USE_FOG

        if (settings.useCenterShadows && settings.canCenterShadows) this._defines.USE_CENTER_SHADOWS = 1
        else delete this._defines.USE_CENTER_SHADOWS

        this._defines.MIPMAP_BIAS = String(settings.mipmapBias) + '.0'

        this._defines.NUM_LIGHTS = settings.numLights || 1
    }

    updateUniforms()  {
    
        let sun = vec3.create()
        vec3.set(sun, 0.0, 0.0, 1.0)
        vec3.rotateX( sun, sun, [0,0,0], -settings.uSunPositionY*Math.PI/2.0 );
        vec3.rotateY( sun, sun, [0,0,0], -settings.uSunPositionX*Math.PI + Math.PI/2    );
        this._uniforms.uSunDirection = sun;
        this._uniforms.uSunPosition = [ settings.uSunPositionX, settings.uSunPositionY ];

        this._uniforms.useBetterTextureSampling     = settings.useBetterTextureSampling?1:0
        

        this._uniforms.uFogHeightFalloff            = settings.uFogHeightFalloff
        this._uniforms.uFogDensity                  = settings.uFogDensity

        this._uniforms.uResolution                  = [ this.resolution[0], this.resolution[1] ];
        this._uniforms.pass                         = 0
        this._uniforms.uCameraPosition              = this.camera.position;
        this._uniforms.uCameraFar                   = this.camera.far;//settings.cameraFar
        this._uniforms.uCameraNear                  = this.camera.near;//settings.cameraNear
        this._uniforms.uNearPleane                  = settings.uNearPleane;
        this._uniforms.useGammaCorrection           = settings.gammaCorrection ? 1 : 0;
        // this._uniforms.usePostGammaCorrection           = settings.usePostGammaCorrection ? 1 : 0;
        this._uniforms.uFocalLength                 = settings.focalLength; 
        this._uniforms.uBoxSize                     = settings.uBoxSize; 
        this._uniforms.uLightSize                   = this.lightSize; 
        this._uniforms.useBakedLighting             = settings.useBakedLighting?1:0
        this._uniforms.uExposition                  = settings.exposition
        this._uniforms.uEnvRotationX                = settings.envRotationX
        this._uniforms.uEnvRotationY                = settings.envRotationY
        this._uniforms.uEnvRotationZ                = settings.envRotationZ
        this._uniforms.IBLResolution                = settings.IBLResolution;
        this._uniforms.useSSShadow                  = settings.useSSShadow?1:0;
        this._uniforms.uSamplingMethod              = settings.samplingMethod
        this._uniforms.uBNSamplingMethod            = settings.blueNoiseSamplingMethod
        this._uniforms.uBlueNoiseResolution         = [64,64]
        this._uniforms.tBNDS                        = this.bndsTextures[ this.bndsTextureIndex ]
        this._uniforms.frameIndex                   = Math.floor(this.frameIndex)
        this._uniforms.uTime                        = Math.floor(this.frameIndex)/100
        this._uniforms.iTime                        = this.time;
        this._uniforms.useShadows                   = settings.useShadows?1:0;
        this._uniforms.useCenterShadows             = settings.useCenterShadows && settings.canCenterShadows ?1:0;
        this._uniforms.uSearchWidth                 = settings.uSearchWidth;
        this._uniforms.uShadowBlur                  = settings.uShadowBlur;
        this._uniforms.uShadowBias                  = settings.uShadowBias;
        this._uniforms.uShadowMix                   = settings.uShadowMix;
        this._uniforms.uPoisonDisk                  = settings.uPoisonDisk
        this._uniforms.useSoftShadows               = settings.useSoftShadows?1:0;
        this._uniforms.useShadowNoise               = settings.useShadowNoise?1:0;
        this._uniforms.useRandomColor               = settings.useRandomColor?1:0;
        this._uniforms.ltc_1                        = this.ltc_1;
        this._uniforms.ltc_2                        = this.ltc_2;
        this._uniforms.useAreaLights                = settings.useAreaLights ? 1 : 0
        this._uniforms.useDerivative                = settings.useDerivative ? 1 : 0
        this._uniforms.useLowDepth                  = settings.useLowDepth ? 1:0
        this._uniforms.useHDR                       = this._useHDR ? 1:0
        this._uniforms.useSky                       = this._useSky ? 1:0
        this._uniforms.useIBL                       = settings.useIBL ? 1:0
        this._uniforms.useSSCheckerboard            = settings.useSSCheckerboard ? 1:0
        this._uniforms.useGbufferCheckerboard       = settings.useGbufferCheckerboard ? 1:0
        this._uniforms.uSceneDownscale              = settings.sceneDownscale
        this._uniforms.uSharpenStrength             = settings.uSharpenStrength;
        this._uniforms.uIBLDiffuseMix               = settings.IBLDiffuseMix;
        this._uniforms.uIBLSpecularMix              = settings.IBLSpecularMix;
        
        // temporal AA by jittering cameraprojection matrix
        this._uniforms.useTemporalAA             = settings.useTemporalAA ? 1 : 0
        this._uniforms.useTemporalJitter         = settings.useTemporalJitter  ? 1 : 0
        this._uniforms.uNeighbourgClippingOffset = settings.neighbourgClippingOffset
        this._uniforms.uVarianceClippingGamma    = settings.uVarianceClippingGamma;
        this._uniforms.useBilateralUpscale       = settings.useBilateralUpscale ? 1 : 0
        if (settings.useTemporalJitter) { 
            if (settings.useTemporalUpscale) {
                let offsets = [
                    -1,  1,
                    1,   1,
                    1,  -1,
                    -1, -1
                ]
                this._uniforms.uTemporalJitter = [ Math.random() * offsets[(this.frameIndex % 4) * 2], Math.random() * offsets[(this.frameIndex % 4) * 2 + 1]]
                // this._uniforms.uTemporalJitter = [ 0.5* offsets[(this.frameIndex % 4) * 2], 0.5* offsets[(this.frameIndex % 4) * 2 + 1]]
            }
            else {
                this._uniforms.uTemporalJitter = [ (Math.random()-0.5)*2, (Math.random()-0.5)*2 ]
            }
        }
        if (settings.animateNoise && settings.blueNoiseSamplingMethod==6) {
            this.bndsTextureIndex++
            this.bndsTextureIndex = this.bndsTextureIndex % 1
        }
        if (settings.animateNoise) {
            this.frameIndex += 1//0.05
            this.frameIndex = this.frameIndex % 256
        }
    }






    //TODO: add proper setters instead of manually setting the values
    // also find a proper approach to avoid overriing profile settings as this is really redondant
    setBloom(useBloom) {
        this.useBloom = useBloom
    }
    
    setSoftShadows(useSoftShadows) {
        this.useSoftShadows = useSoftShadows
    }

    setSkySphereColor(skySphereColor) {
        this.skySphereColor = skySphereColor
    }
    setFog(useFog) {
        settings.useFog = useFog
    }
    setChromaticAberration(useChromaticAberration, chromaticAberrationOffset) {
        this.useChromaticAberration = useChromaticAberration; 
        this.chromaticAberrationOffset = chromaticAberrationOffset;
    }
    setColorcorrection(useColorCorrection) {
        this.useColorCorrection = useColorCorrection
    }
    setIBL(useIBL, IBLDiffuseMix, IBLSpecularMix) { 
        settings.useIBL          = useIBL 
        settings.IBLDiffuseMix  = IBLDiffuseMix
        settings.IBLSpecularMix = IBLSpecularMix
    }
    setHDR(useHDR, hdrMap, blur) { 
        this._useHDR  = useHDR
        if (!this._useHDR) {
            return
        }
    }
    setSky(useSky) {
        this._useSky  = useSky
    }
    setUnjitter(useUnjitter) { this.temporalAARenderPass.useUnjitter = useUnjitter; }
    setVignetting(useVignetting, vignettingColor, vignettingStrength) { 
        settings.useVignetting = useVignetting 
        this.vignettingColor = vignettingColor 
        this.vignettingStrength = vignettingStrength
    }
    setToneMapping(useToneMapping) { 
        this.useToneMapping = useToneMapping;
    }
    setLightSize(lightSize) { this.lightSize = lightSize }
    setClouds(useClouds) { this.useClouds = useClouds; }
    setShadowMapSize(shadowmapSize) { settings.uShadowMapTileSize = shadowmapSize }
    setShadowMapBias(shadowmapBias) { settings.uShadowBias = shadowmapBias; }
    setSSGI(useSSGI) { this.SSRRenderPass.useSSGI = useSSGI }
    setSSR(useSSR) { this.SSRRenderPass.useSSR = useSSR }
    setSSAO(useSSAO) { this.SSRRenderPass.useSSAO = useSSAO }
    setSSDirect(useSSDirect) { this.SSRRenderPass.useSSDirect = useSSDirect }
    setSkySphereRadius(radius) { 
        this.skySphere.scale[0] = 
        this.skySphere.scale[1] =
        this.skySphere.scale[2] = radius
    }

    updateLights() {
        
        //TODO split by light type to reduce num of uniforms
        // let directionalLights = []
        // let spotLights        = []
        // let areaLights        = []

		var lightInvVPMatrix = []
		var lightPMatrix     = []
        var lightMMatrix     = []
        let lightPMMatrix    = []
        var lightColor       = []
        var lightDistance    = []
        var lightPosition    = []
        let lightAngleCos    = []
        let lightDirection   = []
        let ligthRotation    = []
        let lightType        = []
        let lightHalfHeight  = []
        let lightHalfWidth   = []
        let lightCenterPMMatrix = []
        let lightCenterPMatrix = null

        if (this.scene.lights && this.scene.lights.length > 0) {
            for (var i = 0; i < this.scene.lights.length; i++) {

                //save original light type as we will replace area light on lower performance profiles
                //this way we make sure when updating the light that we use the original light type
                if (this.scene.lights[i].realType) {
                    this.scene.lights[i].type = this.scene.lights[i].realType
                    this.scene.lights[i].intensity = this.scene.lights[i].realIntensity
                }
                else {
                    this.scene.lights[i].realType = this.scene.lights[i].type
                    this.scene.lights[i].realIntensity = this.scene.lights[i].intensity
                }
                
                if (this.scene.lights[i].type == 'directionallight' ) {
                    let sun = vec3.create()
                    vec3.set(sun, 0.0, 0.0, 1.0)
                    vec3.rotateX( sun, sun, [0,0,0], -settings.uSunPositionY*Math.PI/2.0 );
                    vec3.rotateY( sun, sun, [0,0,0], -settings.uSunPositionX*Math.PI + Math.PI/2    );
                    vec3.normalize( sun, sun )
                    vec3.scale( sun, sun, 50000 )
                    this.scene.lights[i].position[0] = sun[0] * settings.sceneDownscale;
                    this.scene.lights[i].position[1] = sun[1] * settings.sceneDownscale;
                    this.scene.lights[i].position[2] = sun[2] * settings.sceneDownscale;
                    if (this.scene.sunHelper) {
                        this.scene.sunHelper.position[0] = sun[0] * 4  * settings.sceneDownscale;
                        this.scene.sunHelper.position[1] = sun[1] * 4  * settings.sceneDownscale;
                        this.scene.sunHelper.position[2] = sun[2] * 4  * settings.sceneDownscale;
                    }
                }

                this.scene.lights[i].update();
                
                if (this.scene.lights[i].shadowMap) {

                    lightMMatrix.push.apply(lightMMatrix, this.scene.lights[i].shadowMapCamera.inverseWorldMatrix);
                    lightPMatrix.push.apply(lightPMatrix, this.scene.lights[i].shadowMapCamera.projectionMatrix);    
                    
                    let lmp = mat4.create()
                    mat4.multiply(lmp, this.scene.lights[i].shadowMapCamera.projectionMatrix, this.scene.lights[i].shadowMapCamera.inverseWorldMatrix)
                    lightPMMatrix.push.apply(lightPMMatrix, lmp);

                    mat4.copy(this.lightInvVPMatrix, this.scene.lights[i].shadowMapCamera.projectionMatrix);
                    mat4.multiply(this.lightInvVPMatrix, this.lightInvVPMatrix, this.scene.lights[i].shadowMapCamera.inverseWorldMatrix);
                    mat4.invert(this.lightInvVPMatrix, this.lightInvVPMatrix);
                    lightInvVPMatrix.push.apply(lightInvVPMatrix, this.lightInvVPMatrix);
                }
                
                //only retrieve once for all center camera as all projection will be the same:
                //all center shadow camera use angle of (1.-uSunAngle)*PI
                if (settings.useCenterShadows
                    && settings.canCenterShadows 
                    && this.scene.lights[i].shadowMapCenterCamera) {
                    let lmp = mat4.create()
                    mat4.multiply(lmp, this.scene.lights[i].shadowMapCenterCamera.projectionMatrix, this.scene.lights[i].shadowMapCamera.inverseWorldMatrix)
                    lightCenterPMMatrix.push.apply(lightCenterPMMatrix, lmp);
                }
                
                ligthRotation.push.apply(ligthRotation, this.scene.lights[i]._normalMatrix);

                lightPosition.push(
                    this.scene.lights[i].worldMatrix[12], this.scene.lights[i].worldMatrix[13], this.scene.lights[i].worldMatrix[14]
                );

                if (this.scene.lights[i].type == 'directionallight') {
                    let d = vec3.create()
                    vec3.set(d, 0, 0, 0)
                    vec3.subtract(d, this.scene.lights[i].position, d);
                    vec3.normalize( d, d );
                    lightDirection.push(d[0], d[1], d[2]);                    
                }
                else {
                    let d = vec3.create()
                    vec3.set(d, 0,0, -1);
                    vec3.transformMat4( d, d, this.scene.lights[i]._normalMatrix )
                    vec3.normalize( d, d );
                    lightDirection.push( d[0], d[1], d[2] );
                }

                //replace spotlight with area light and compensate the intesity
                if (this.scene.lights[i].type === 'arealight' && !settings.useAreaLights) {
                    this.scene.lights[i].type = 'spotlight'
                    this.scene.lights[i].intensity = this.scene.lights[i].intensity == 20 ? 180 : this.scene.lights[i].intensity;
                }

                let intensity = this.scene.lights[i].intensity;
                if (this.scene.lights[i].type === 'arealight' && settings.useAreaLights) {
                    intensity *= settings.uAreaLightIntensity; 
                }
                else if (this.scene.lights[i].type === 'spotlight' ) {
                    intensity *= settings.uSpotlightIntensity;
                }
                // if (this.scene.lights[i].type === 'directionallight') {
                //     intensity = 0.0008 * settings.uSunIntensity;
                // }

                let c = [
                    this.scene.lights[i].color[0] * intensity,
                    this.scene.lights[i].color[1] * intensity,
                    this.scene.lights[i].color[2] * intensity
                ]

                //DEBUG
                if (this.scene.lights[i].type == 'arealight') {
                    this.scene.lights[i].angle = settings.uSunAngle * Math.PI/2
                }

                lightAngleCos.push( Math.cos( this.scene.lights[i].angle ) );
                lightColor.push(c[0], c[1], c[2])
                lightDistance.push(this.scene.lights[i].distance)
                lightType.push( this.scene.lights[i].type == 'arealight' ? 1 : this.scene.lights[i].type == 'directionallight' ? 2 : 0 )//0== spotlight

                //compute 4 points position in world space for each rect area light
                let halfWidth = vec3.create()
                let halfHeight = vec3.create()
                vec3.set( halfWidth,  this.scene.lights[i].width * 0.5, 0,  0. );
                vec3.set( halfHeight, 0,  this.scene.lights[i].height * 0.5, 0.0 );
                let matrix4 = mat4.create()
                let q = quat.create()
				mat4.copy( matrix4, this.scene.lights[i].worldMatrix );
				// mat4.multiply( matrix4, this.camera.inverseWorldMatrix, matrix4 );
                mat4.getRotation(q, matrix4) 
                vec3.transformQuat( halfWidth, halfWidth, q );
                vec3.transformQuat( halfHeight, halfHeight, q );
                lightHalfWidth.push.apply( lightHalfWidth, halfWidth )
                lightHalfHeight.push.apply( lightHalfHeight, halfHeight )
                
            }
        }

        settings.numLights = this.scene.lights.length

        this._uniforms.uNumLights         = this.scene.lights.length;
        this._uniforms.lightType          = lightType;

        this._uniforms.lightInvVPMatrix   = lightInvVPMatrix;
        this._uniforms.lightPMatrix       = lightPMatrix;
        this._uniforms.lightMMatrix       = lightMMatrix;
        this._uniforms.lightPMMatrix       = lightPMMatrix;
        this._uniforms.lightColor         = lightColor;
        this._uniforms.lightDistance      = lightDistance;
        this._uniforms.lightPosition      = lightPosition;
        this._uniforms.lightDirection     = lightDirection;
        this._uniforms.lightAngleCos      = lightAngleCos;
        this._uniforms.ligthRotation      = ligthRotation;
        this._uniforms.lightHalfWidth     = lightHalfWidth;
        this._uniforms.lightHalfHeight    = lightHalfHeight;
        
        if (settings.useCenterShadows && settings.canCenterShadows) {
            this._uniforms.uLightCenterPM = lightCenterPMatrix;
            this._uniforms.lightCenterPMMatrix = lightCenterPMMatrix;
        }

    }


    // updateReflectionProbes() {
    //     if ( !this.scene.reflectionsProbes || !this.scene.reflectionsProbes[ this.reflectionProbeIndex ]  ) {
    //         return
    //     }
    //     let atlasSize      = settings.reflectionProbeResolution;
    //     let tileSize       = settings.reflectionProbeTileSize;
    //     let tilePerSide    = atlasSize / tileSize;
    //     let shadowMapIndex = Math.floor(this.reflectionProbeIndex / (tilePerSide*tilePerSide))
    //     let ii             = this.reflectionProbeIndex - ((tilePerSide*tilePerSide)*shadowMapIndex)
    //     let x              =  ii % tilePerSide;
    //     let y              =  Math.floor(ii / tilePerSide);
    //     this._uniforms['uReflectionProbeResolution'] = atlasSize;
    //     this._uniforms['uReflectionProbeTileSize']   = tileSize;
        
    //     this.scene.reflectionsProbes[ this.reflectionProbeIndex ].update();
    //     this.renderer.gl.enable(this.renderer.gl.SCISSOR_TEST);
    //     this.renderer.gl.scissor(x*tileSize, y*tileSize, tileSize, tileSize);
    //     this.renderer.gl.viewport(x*tileSize, y*tileSize, tileSize, tileSize);
    //     this.renderer.clearColor(-1.,-1.,0,0);
    //     this.renderer.clear() 
    //     this.renderer.render(this.scene, this.scene.lights[ this.reflectionProbeIndex ].shadowMapCamera, this.reflectionProbeFrameBuffer );
    //     debugData.renderPasses.push({name: 'reflectionProbeRenderPass', buffer: this['shadowMap'+shadowMapIndex], uniforms:this._uniforms })
    //     this._uniforms['tReflectionProbe']  = this.reflectionProbeFrameBuffer
    // }


    updateVirtualTextures() {

        settings.isVirtualTexturePass = true;

        let skySphereState = this.skySphere.visible
        this.skySphere.visible = false


        this.renderer.gl.disable(this.renderer.gl.CULL_FACE);

        this.renderer.clearColor(0.,0.,0,1);
        this.renderer.clear() 
        this._uniforms.uVirtualTextureResolution = [settings.virtualTextureResolution, settings.virtualTextureResolution]
        this._uniforms.uVirtualTexturePass = 1
        this.renderer.render(this.scene, this.camera, this.virtualTextureFrameBuffer );
        settings.useEditor && debugData.renderPasses.push({name: 'virtualTextureRenderPass', buffer: this.virtualTextureFrameBuffer })
        
        //restore previous state
        //TODO: automate this
        this._uniforms.uVirtualTexturePass = 0
        this.skySphere.visible = skySphereState
        settings.isVirtualTexturePass = false;
    }


    updateShadowMaps() {
    
        /** 
         * Only render 1 light per frame for performances reasons
         * As we draw using gl.scissor in the atlas we dont need to update all shadow maps, only if objects moved
         */
        if (settings.forceShadows || (
                settings.updateShadows
                && settings.useShadows
                && this.currentLightIndex < this.scene.lights.length 
                && this.scene.lights[ this.currentLightIndex ] 
                )
            ) {

            // if (settings.backfaceCulling) {
            //     if (!this.renderer.gl.IS_CULL_FACE) {
            //         this.renderer.gl.IS_CULL_FACE = true;
            //         this.renderer.gl.enable(this.renderer.gl.CULL_FACE);
            //         this.renderer.gl.cullFace(this.renderer.gl.BACK);
            //     }
            // }
            // else {
            //     if (this.renderer.gl.IS_CULL_FACE) {
            //         this.renderer.gl.IS_CULL_FACE = false;
            //         this.renderer.gl.disable(this.renderer.gl.CULL_FACE);
            //     }
            // }

            this.scene.lights[ this.currentLightIndex ].update();

            let atlasSize     = settings.uShadowMapResolution;
            let tileSize      = settings.uShadowMapTileSize;
            let tilePerSide   = atlasSize / tileSize;
            let shadowMapIndex = Math.floor(this.currentLightIndex / (tilePerSide*tilePerSide))
            let ii = this.currentLightIndex - ((tilePerSide*tilePerSide)*shadowMapIndex)
            let x =  ii % tilePerSide;
            let y =  Math.floor(ii / tilePerSide);
            this.renderer.gl.enable(this.renderer.gl.SCISSOR_TEST);
            this.renderer.gl.scissor(x*tileSize, y*tileSize, tileSize, tileSize);
            this.renderer.gl.viewport(x*tileSize, y*tileSize, tileSize, tileSize);
            this._uniforms['tShadowMap'+shadowMapIndex] = this.readGFrameBuffer;
            this._uniforms['uShadowMapResolution'] = settings.uShadowMapResolution;
            this._uniforms['uShadowMapTileSize']   = settings.uShadowMapTileSize;

            this.renderer.clearColor(-1.,-1.,0,0);
            this.renderer.clear() 
            this._uniforms.uShadowMapPass     = 1

            // if (this.scene.sunHelper) {
            //     this.scene.sunHelper.visible = false;
            // }
            
            this.renderer.render(this.scene, this.scene.lights[ this.currentLightIndex ].shadowMapCamera, this['shadowMap'+shadowMapIndex] );
            settings.useEditor && debugData.renderPasses.push({name: 'shadowMapRenderPass', buffer: this['shadowMap'+shadowMapIndex], uniforms:this._uniforms })

            this._uniforms['tShadowMap'+shadowMapIndex]  = this['shadowMap'+shadowMapIndex]
            
            if (this.scene.lights[ this.currentLightIndex ].shadowMapCenterCamera) {
                let atlasSize     = settings.uShadowMapResolution;
                let tileSize      = settings.uShadowMapCenterTileSize;
                let tilePerSide   = atlasSize / tileSize;
                let shadowMapIndex = Math.floor(this.currentLightIndex / (tilePerSide*tilePerSide))
                let ii = this.currentLightIndex - ((tilePerSide*tilePerSide)*shadowMapIndex)
                let x =  ii % tilePerSide;
                let y =  Math.floor(ii / tilePerSide);
                this.renderer.gl.scissor(x*tileSize, y*tileSize, tileSize, tileSize);
                this.renderer.gl.viewport(x*tileSize, y*tileSize, tileSize, tileSize);
                this._uniforms['tCenterShadowMap'] = this.readGFrameBuffer;
                this._uniforms['uShadowMapCenterTileSize'] = settings.uShadowMapCenterTileSize;
                this.renderer.render(this.scene, this.scene.lights[ this.currentLightIndex ].shadowMapCenterCamera, this.shadowMapCenter );
                this._uniforms['tCenterShadowMap']  = this.shadowMapCenter
                settings.useEditor && debugData.renderPasses.push({name: 'centerShadowMapRenderPass', buffer: this.shadowMapCenter })
                // console.log('RENDER CENTER SHADOW MAP', this.currentLightIndex, this.scene.lights[ this.currentLightIndex ].shadowMapCenterCamera.near, this.scene.lights[ this.currentLightIndex ].shadowMapCenterCamera.far);
            }

            this.renderer.gl.disable(this.renderer.gl.SCISSOR_TEST);
            this._uniforms.uShadowMapPass = 0

            // if (this.scene.sunHelper) {
            //     this.scene.sunHelper.visible = true;
            // }

            this.currentLightIndex++
            if (this.currentLightIndex>this.scene.lights.length-1) {
                this.currentLightIndex = 0
                this.shadowMapUpdateRounds++
                //TODO: wait for scene to be redy before doing this
                if (this.shadowMapUpdateRounds>=0) {
                    settings.updateShadows = false
                    this.currentLightIndex = 0
                    this.shadowMapUpdateRounds = 0
                }
            }

        }

    }

    resetCameraStatus() {
        clearTimeout(this.cameraTimer)
        this.cameraTimer = setTimeout(()=>{
            this.hasCameraMoved = false
            settings.useNeighbourgClipping = false
        },10)
    }


    // resetCameraStatus2() {
    //     clearTimeout(this.cameraTimer2)
    //     this.cameraTimer2 = setTimeout(()=>{
    //         this.hasCameraRotate = false
    //         if (this.temporalAARenderPass) this.temporalAARenderPass.temporalThreshold = 0.9
    //     },10)
    // }

    updateCameras() {

    
        if (this._uniforms.uProjViewMatrix) {
            this._uniforms.uLastVPMatrix = this._uniforms.uLastVPMatrix || mat4.create()
            this._uniforms.uLastInvVPMatrix = this._uniforms.uLastInvVPMatrix || mat4.create()
            mat4.copy(this._uniforms.uLastVPMatrix, this.camera.projectionMatrix);
            mat4.multiply(this._uniforms.uLastVPMatrix, this._uniforms.uLastVPMatrix, this.camera.inverseWorldMatrix );
            mat4.invert(this._uniforms.uLastInvVPMatrix, this._uniforms.uLastVPMatrix);
            this._uniforms.uOldEyePosition    = this._uniforms.uOldEyePosition || [0,0]
            this._uniforms.uOldEyeDirection    = this._uniforms.uOldEyeDirection || [0,0]
            vec3.copy( this._uniforms.uOldEyePosition, this._uniforms.uEyePosition )
            vec3.copy( this._uniforms.uOldEyeDirection, this._uniforms.uEyeDirection )
        }

        this.camera.update();
        this.bufferCamera.update();

        
        if (settings.autoNeighbourgClipping) {
            if ( 
                !this.hasCameraMoved 
                && ( 
                    Math.abs(this.cameraPosition[0] - this.camera.worldMatrix[12]) > (0.1 * settings.sceneDownscale) 
                    || Math.abs(this.cameraPosition[1] - this.camera.worldMatrix[13]) > (0.1 * settings.sceneDownscale) 
                    || Math.abs(this.cameraPosition[2] - this.camera.worldMatrix[14]) > (0.1 * settings.sceneDownscale)
                )
            ) {
                this.hasCameraMoved = true
                settings.useNeighbourgClipping = true
                this.resetCameraStatus()
            }
        }
        
        


        //camera world position
        vec3.set(this.cameraPosition, this.camera.worldMatrix[12], this.camera.worldMatrix[13], this.camera.worldMatrix[14]);

        //camera world direction
        vec3.copy(this.cameraDirection, this.camera.lookAt);
        vec3.subtract(this.cameraDirection, this.cameraDirection, this.cameraPosition);
        vec3.normalize(this.cameraDirection, this.cameraDirection);

                
        /** when not using "catmul" sampling, we chnage the temporal theshold of the TAA based on camera movment to reduce the blur created by accumulated errors */
        if (this.temporalAARenderPass && !settings.useBetterTextureSampling) {
            let tempRatio = Math.max(0, Math.min(1, (1 - ( Math.min(1,Math.abs(vec3.dot(this._oldCameraDirection, this.cameraDirection))) ))  /  (1 - 0.9999999) ))
            this.temporalAARenderPass.temporalThreshold = this.hasCameraMoved ? 0.6 :  0.6 + (0.3 * (1-tempRatio));
        }


        vec3.copy( this._oldCameraDirection, this.cameraDirection ) 

        //camera projection-view matrix and inverse
        mat4.copy(this.viewProjectionwMatrix, this.camera.projectionMatrix);
        mat4.multiply(this.viewProjectionwMatrix, this.viewProjectionwMatrix, this.camera.inverseWorldMatrix );
        mat4.invert(this.inverseViewProjectionMatrix, this.viewProjectionwMatrix);

        //set camera related uniforms
        this._uniforms.uInverseViewProjectionMatrix = this.inverseViewProjectionMatrix;
        this._uniforms.uProjViewMatrix = this.viewProjectionwMatrix
        this._uniforms.uEyePosition    = this.cameraPosition;
        this._uniforms.uEyeDirection   = this.cameraDirection;

        this._uniforms.uInverseProjectionMatrix = this._uniforms.uInverseProjectionMatrix || mat4.create()
        this._uniforms.uInverseViewMatrix       = this._uniforms.uInverseViewMatrix  || mat4.create()
        mat4.copy(this._uniforms.uInverseViewMatrix, this.camera.worldMatrix);
        mat4.invert(this._uniforms.uInverseProjectionMatrix, this.camera.projectionMatrix);
        
    }

    // renderPositionPass() {
    //     this._uniforms.tPosition  = this.blankFrameBuffer
    //     this._uniforms.uPositionPass  = 1;
    //     this.renderer.clearColor(0,0,0,0);
    //     this.renderer.clear() 
    //     this.renderer.render(this.scene, this.camera, this.writePositionFrameBuffer)
    //     let tmp = this.readPositionFrameBuffer;
    //     this.readPositionFrameBuffer = this.writePositionFrameBuffer;
    //     this.writePositionFrameBuffer = tmp;
    //     this._uniforms.tPosition     = this.readPositionFrameBuffer;
    //     this._uniforms.uPositionPass  = 0;
    // }


    renderGPass() {



        this._uniforms.tDepth      = this.blankFrameBuffer
        
        /** FRONTFACE (depth, N, Rgh/mtln/ior) */
        // if (settings.backfaceCulling) {
            if (!this.renderer.gl.IS_CULL_FACE) {
                this.renderer.gl.IS_CULL_FACE = true;
                this.renderer.gl.enable(this.renderer.gl.CULL_FACE);
                this.renderer.gl.cullFace(this.renderer.gl.BACK);
            }
        // }
        // else {
        //     if (this.renderer.gl.IS_CULL_FACE) {
        //         this.renderer.gl.IS_CULL_FACE = false;
        //         this.renderer.gl.disable(this.renderer.gl.CULL_FACE);
        //     }
        // }


        this._uniforms.uDepthPass  = 1;
        // this.renderer.clearColor(-1,0,0,0);
        // this.renderer.clear() 
        this._uniforms.uDepthResolution = [ this.resolution[0],  this.resolution[1] ];
        this.renderer.render(this.scene, this.camera, this.writeGFrameBuffer)
        settings.useEditor && debugData.renderPasses.push({name: 'GBufferRenderPass', buffer: this.writeGFrameBuffer })

        let tmp = this.readGFrameBuffer;
        this.readGFrameBuffer = this.writeGFrameBuffer;
        this.writeGFrameBuffer = tmp;

        this._uniforms.tDepth     = this.readGFrameBuffer;
        this._uniforms.uDepthPass  = 0;
    }

    resize(w, h) {

        w = nearestOf2(w)
        h = nearestOf2(h)

        this.resolution[0] = Math.floor(w * settings.renderScale)
        this.resolution[1] = Math.floor(h * settings.renderScale)

		this.camera.aspect = this.resolution[0] / this.resolution[1]
		this.camera.updateProjectionMatrix()
        this.bufferPlane.scale[0] = this.resolution[0]
        this.bufferPlane.scale[1] = this.resolution[1]
        this.bufferCamera.left    = -this.resolution[0]/2
        this.bufferCamera.right   = this.resolution[0]/2
        this.bufferCamera.top     = this.resolution[1]/2
        this.bufferCamera.bottom  = -this.resolution[1]/2
        this.bufferCamera.updateProjectionMatrix()
        
        this._uniforms.uResolution = [this.resolution[0] , this.resolution[1] ];

        this.depthFrameBufferFront && this.depthFrameBufferFront.resize(this.resolution[0],this.resolution[1])
        this.depthFrameBufferBack && this.depthFrameBufferBack.resize(this.resolution[0],this.resolution[1])
        this.lastDepthFrameBuffer && this.lastDepthFrameBuffer.resize(this.resolution[0],this.resolution[1])
    
        // if (this.IBLFrameCount < 50) {
        //     //diffuse
        //     this.diffuseIBLFrameBuffer && this.diffuseIBLFrameBuffer.resize( settings.IBLResolution[0],   settings.IBLResolution[1] )
        //     this.diffuseIBLDenoiserPass && this.diffuseIBLDenoiserPass.resize(  settings.IBLResolution[0],   settings.IBLResolution[1])
        //     this.denoisedDiffuseIBLFrameBuffer && this.denoisedDiffuseIBLFrameBuffer.resize( settings.IBLResolution[0],   settings.IBLResolution[1] )
        //     //specular
        //     for (let i=0; i<5; i++) {
        //         let IBLW =  i == 0 ? 2048 : settings.IBLResolution[0]    
        //         let IBLH =  i == 0 ? 1024 : settings.IBLResolution[1]    
        //         this['specularIBLFrameBuffer'+i] && this['specularIBLFrameBuffer'+i].resize(  IBLW,   IBLH)
        //         this['specularIBLDenoiserPass'+i] && this['specularIBLDenoiserPass'+i].resize(  IBLW,   IBLH)
        //         this['denoisedSpecularIBLFrameBuffer'+i] && this['denoisedSpecularIBLFrameBuffer'+i].resize( IBLW,   IBLH )
        //     }
        // }

        // useSSDownscale is a simple downscale multiplier
        // this is just to compare performances with checkerboard
        this.SSRRenderPass && this.SSRRenderPass.resize( this.resolution[0] * (settings.useSSDownscale?0.5:1),this.resolution[1] * (settings.useSSDownscale?0.5:1) )


        // this.dofRenderPass && this.dofRenderPass.resize( this.resolution[0],this.resolution[1] )
        this.bloomRenderPass && this.bloomRenderPass.resize( this.resolution[0],this.resolution[1] )
        
        this.smaaRenderPass && this.smaaRenderPass.resize( this.resolution[0],this.resolution[1] )

        this.debugFrameBuffer.resize(this.resolution[0],this.resolution[1])
        this.debugFrameBuffer.resize(this.resolution[0],this.resolution[1])
        this.reflectionProbeFrameBuffer.resize(this.resolution[0],this.resolution[1])


        this.alphaFrameBuffer.resize(this.resolution[0],this.resolution[1])
        this.frontFrameBuffer.resize(this.resolution[0],this.resolution[1])
        this.backFrameBuffer.resize(this.resolution[0],this.resolution[1])
        // this.chromaticAberrationPass.resize( this.resolution[0],this.resolution[1] )
        this.toneMappingRenderPass.resize( this.resolution[0],this.resolution[1] )


        // !FULL SCALE
        let whd = settings.useTemporalUpscale ? w : this.resolution[0];
        let hhd = settings.useTemporalUpscale ? h : this.resolution[1];
        
        this.temporalAARenderPass.resize(whd,hhd)
        this.cloudsPass.resize( whd,hhd)
        this.frontHDFrameBuffer.resize(whd,hhd);
        this.backHDFrameBuffer.resize(whd,hhd);
    }

    swapBuffers() {
        let tmp = this.readBuffer;
        this.readBuffer = this.writeBuffer;
        this.writeBuffer = tmp;
    }

    swapHDBuffers() {
        let tmp = this.readHDBuffer;
        this.readHDBuffer = this.writeHDBuffer;
        this.writeHDBuffer = tmp;
    }

    render() {



        //clear Render passes

        if (!this.scene) {
            return
        }

        this.time += 0.01


        this.updateCameras()

        if (this.lightsNeedUpdate || settings.updateIBL || settings.forceShadows ) {
            this.updateLights()
            this.lightsNeedUpdate = false;
        }

        if (this.numRenderedFrames > 2 && (settings.updateShadows || settings.forceShadows)) {
            this.updateShadowMaps();
            return;
        }


        // if (this.numRenderedFrames > 2 && (settings.updateReflectionProbes)) {
        //     this.updateReflectionProbes();
        //     return;
        // }


        // this.updatePointerRay()
        this.updateDefines()
        this.updateUniforms()

        if (!this.hasrenderedWorley) {
            /** WORLEY NOISE */
            this.bufferPlane.material =  this.worleyMaterial;
            this.renderer.clearColor(0,0,0,1)
            this.renderer.clear()
            this._uniforms.uWorleyScale =  settings.uWorleyTextureScale0;
            this.renderer.render(this.bufferScene,  this.bufferCamera, this.worleyFrameBuffer0 );
            debugData.renderPasses.push({name: 'worleyBuffer0RenderPass', buffer: this.worleyFrameBuffer0 })
            this.renderer.clear()
            this._uniforms.uWorleyScale =  settings.uWorleyTextureScale1;
            this.renderer.render(this.bufferScene,  this.bufferCamera, this.worleyFrameBuffer1 );
            debugData.renderPasses.push({name: 'worleyBuffer0RenderPas1', buffer: this.worleyFrameBuffer1 })
            this.renderer.clear()
            this._uniforms.uWorleyScale =  settings.uWorleyTextureScale2;
            this.renderer.render(this.bufferScene,  this.bufferCamera, this.worleyFrameBuffer2 );
            debugData.renderPasses.push({name: 'worleyBuffer0RenderPass2', buffer: this.worleyFrameBuffer2 })
            this._uniforms.tWorleyNoise0 =  this.worleyFrameBuffer0;
            this._uniforms.tWorleyNoise1 =  this.worleyFrameBuffer1;
            this._uniforms.tWorleyNoise2 =  this.worleyFrameBuffer2;
            this.hasrenderedWorley = true;
            return;
        }


        /** CONVOLUTION PASS */
		if (settings.updateIBL || (/*(this.isHDRLoaded || !this._useHDR) &&*/ this.IBLFrameCount < 50 ) ) {

            // console.log('UPDATE_IBL');

            settings.isUpdatingIBL = true

            this.IBLFrameCount++
            
            // if (this._useSky || !this._useHDR)  {
                this.hdrSky.render( this.bufferScene, this.bufferPlane, this.bufferCamera )  
                this._uniforms.tEquirectangularMap = this.hdrSky.frameBuffer;
                debugData.renderPasses.push({name: 'skyBufferRenderPass', buffer: this.hdrSky.frameBuffer })

            // }
            // else if (this._useHDR) {
            //     this._uniforms.tEquirectangularMap = this.HDRTexture
            // }

            this.bufferPlane.material          =  this.convoltionMaterial;
            this.renderer.clearColor(0,0,0,1)
            this.renderer.clear()
            for (let i=0;i<5; i++) {
                // if (this.IBLFrameCount> 2 && i==0 && !settings.updateIBL) {
                //     continue;
                // }
                this._uniforms['tSpecularEnv'+i] =  this.readGFrameBuffer;
                this._uniforms.uIBLRoughness = 0.25 * i
                this.renderer.render(this.bufferScene,  this.bufferCamera, this['specularIBLFrameBuffer'+i] );

                debugData.renderPasses.push({name: 'specularIBLBufferRenderPass'+i, buffer: this['specularIBLFrameBuffer'+i] })

                if (i==0) {
                    this._uniforms['tSpecularEnv'+i]  =  this['specularIBLFrameBuffer'+i]
                }
                else {
                    this['specularIBLDenoiserPass'+i] && this['specularIBLDenoiserPass'+i].render( this.bufferScene, this.bufferPlane, this.bufferCamera, this['specularIBLFrameBuffer'+i], this['denoisedSpecularIBLFrameBuffer'+i] );
                    this._uniforms['tSpecularEnv'+i]  =  this['denoisedSpecularIBLFrameBuffer'+i]
                    this.bufferPlane.material          =  this.convoltionMaterial;
                }
            }
            this._uniforms.tDiffuseEnv    =  this.readGFrameBuffer;
            this._uniforms.uIBLRoughness  = settings.uIBLDiffRoughness
            this.renderer.render(this.bufferScene,  this.bufferCamera, this.diffuseIBLFrameBuffer );
            debugData.renderPasses.push({name: 'diffuseIBLBufferRenderPass', buffer: this.diffuseIBLFrameBuffer })
            this._uniforms.tDiffuseEnv    =  this.diffuseIBLFrameBuffer
            this.diffuseIBLDenoiserPass && this.diffuseIBLDenoiserPass.render( this.bufferScene, this.bufferPlane, this.bufferCamera, this.diffuseIBLFrameBuffer, this.denoisedDiffuseIBLFrameBuffer );
            this._uniforms.tDiffuseEnv  =  this.denoisedDiffuseIBLFrameBuffer
            debugData.renderPasses.push({name: 'denoisedDiffuseIBLBufferRenderPass', buffer: this.denoisedDiffuseIBLFrameBuffer })
            // if (!settings.debugSky) {
            //     return;
            // }
        }
        else {
            settings.isUpdatingIBL = false
        }



        /** Update sky sphere */
        if (this.skySphere) {
            if (this._useHDR || this._useSky )  {
                this.skySphere.uniforms.uColor = [0,0,0]
                this.skySphere.uniforms.uEmissiveColor = [0,0,0]
                this.skySphere.uniforms.useEmissiveMap = 1
                this.skySphere.uniforms.tEmissive = /*this.blurHDR ? this.denoisedSpecularIBLFrameBuffer1 : */this.specularIBLFrameBuffer0

                //note we use Chroma key color for the sky to better mix clouds
                // if ( settings.deferedRendering && settings.useClouds && this.useClouds ) {
                //     this.skySphere.uniforms.useEmissiveMap = 0
                //     this.skySphere.uniforms.uColor         = [0.05, 0.63, 0.14]
                //     this.skySphere.uniforms.uEmissiveColor = [0.05, 0.63, 0.14]
                // }
            }
            else {
                this.skySphere.uniforms.useEmissiveMap = 0
                this.skySphere.uniforms.uColor         = this.skySphereColor// = [177./255., 185./255., 198./255.]
                this.skySphere.uniforms.uEmissiveColor = this.skySphereColor//[177./255., 185./255., 198./255.]
            }
        }

        let numAlphaMeshes = 0
        // Hide alpha blend meshes
        nestedEach(this.scene, (obj)=>{
            if (obj.material) {
                obj.visible = obj.material.blend ? false : true;
                if (obj.material.blend)  numAlphaMeshes++
            }
        });



        /**
         * VIRTUAL TEXTURES
         */
        if (settings.useVirtualTexture) {
            this.updateVirtualTextures()
        }

        // console.log('FORWARD_RENDERING', settings.forwardRendering, this.writeBuffer)


        if (settings.forwardRendering) {
            this.renderer.clearColor(177./255., 185./255., 198./255.,1);
            this.renderer.clear() 
            this.renderer.render(this.scene, this.camera, this.writeBuffer)

            this.swapBuffers();
        }
        else {
            
            /** DEPTH PASS */
            this.renderGPass()
            

            /** ALPHA PASS */


            if (settings.useAlpha) {
                if (settings.deferedRendering) {
                    if (numAlphaMeshes>0 ) {
                        nestedEach(this.scene, (obj)=>{
                            if (obj.material) {
                                obj.visible = obj.material.blend ? true : false;
                            }
                        });
                        this.renderer.clearColor(0,0,0,0)
                        this.renderer.clear()
                        this._uniforms.uAlphaResolution = [this.resolution[0] , this.resolution[1] ];
                        this._uniforms.tAlphaDepth      = this.readGFrameBuffer;
                        this.renderer.render(this.scene, this.camera, this.alphaFrameBuffer)
                        if (settings.useEditor) {
                            debugData.renderPasses.push({name: 'alphaRenderPass', buffer: this.alphaFrameBuffer })
                        }
                        nestedEach(this.scene, (obj)=>{
                            if (obj.material) {
                                obj.visible = obj.material.blend ? false : true;
                            }
                        });
                    }
                }
            }





            /** Defered shading / SSR / SSGI / SSAO */
            if (settings.deferedRendering) {
                this.SSRRenderPass.render( this.bufferScene, this.bufferPlane, this.bufferCamera, this.readBuffer, this.writeBuffer, this.readGFrameBuffer, this.lastDepthFrameBuffer )  
                this.swapBuffers();
            }
            

                // /** SMAA */
                if (settings.useSMAA) {
                    this.bufferPlane.id = 'SMAARenderPass'
                    this.bufferPlane.name = 'SMAARenderPass'
                    this.smaaRenderPass.render( this.bufferScene, this.bufferPlane, this.bufferCamera,  this.readBuffer, this.writeBuffer,  this.readGFrameBuffer );
                    if (settings.useEditor) {
                        debugData.renderPasses.push({name: 'SMAARenderPass', buffer: this.writeBuffer })
                    }
                    this.swapBuffers();
                }
    


            //compose alpha after temporal AA to avoid ghosting
            if (settings.useAlpha) {
                //ALPHA COMPOSE 
                if (settings.deferedRendering && numAlphaMeshes>0) {
                    this.bufferPlane.material = this.alphaComposeMaterial
                    this.bufferPlane.id = 'AlphaComposRenderePass'
                    this.bufferPlane.name = 'AlphaComposeRenderPass'
                    this.alphaComposeMaterial.setUniform('tAlpha', this.alphaFrameBuffer);
                    this.alphaComposeMaterial.setUniform('tInput', this.readBuffer);
                    this.renderer.render( this.bufferScene, this.bufferCamera, this.writeBuffer );
                    
                    settings.useEditor && debugData.renderPasses.push({name: 'AlphaComposeRenderPass', buffer: this.writeBuffer })
                    this.swapBuffers();
                }
            }
    

    
                if (settings.cloudsBeforeTAA) {
                    /** Clouds */
                    if (settings.deferedRendering && settings.useClouds && this.useClouds) {
                        this.bufferPlane.id = 'CloudsRenderPass'
                        this.bufferPlane.name = 'CloudsRenderPass'
                        this.cloudsPass.render( this.bufferScene, this.bufferPlane, this.bufferCamera,  this.readBuffer, this.writeBuffer,  this.readGFrameBuffer, this.lastDepthFrameBuffer );

                        this.swapBuffers();
                    }
                }
    
                /** Temporal AA */
                if (settings.useTemporalAA) {
                    this.bufferPlane.id = 'TAAPass' 
                    this.bufferPlane.name = 'TAAPass'
                    // this.temporalAARenderPass.temporalThreshold = settings.TAATemporalThreshold
                    this.temporalAARenderPass.overrideSpatialThreshold = settings.TAASpatialThreshold
                    this.temporalAARenderPass.render( this.bufferScene, this.bufferPlane, this.bufferCamera, this.readBuffer, this.writeHDBuffer, this.readGFrameBuffer, this.lastDepthFrameBuffer);
                    settings.useEditor && debugData.renderPasses.push({name: 'TAARenderPass', buffer: this.writeBuffer })
                    this.swapHDBuffers();
                }

    

    
                if (!settings.cloudsBeforeTAA) {
                    /** Clouds */
                    if (settings.deferedRendering && settings.useClouds && this.useClouds) {
                        this.bufferPlane.id = 'CloudsRenderPass'
                        this.bufferPlane.name = 'CloudsRenderPass'
                        this.cloudsPass.render( this.bufferScene, this.bufferPlane, this.bufferCamera,  this.readHDBuffer, this.writeHDBuffer,  this.readGFrameBuffer, this.lastDepthFrameBuffer );
                        settings.useEditor && debugData.renderPasses.push({name: 'CloudsRenderPass', buffer: this.writeHDBuffer })
                        settings.useEditor && debugData.renderPasses.push({name: 'cloudsInfoFrameBuffer', buffer: this.cloudsPass.cloudsInfoFrameBuffer })
                        settings.useEditor && debugData.renderPasses.push({name: 'cloudsResolveFrameBuffer', buffer: this.cloudsPass.resolveFrameBuffer })
                        this.swapHDBuffers();
                    }
                }
        
                // if (!settings.extraPerfBoost) {
                    /** Update Last frame G-Buffer */
                    this.bufferPlane.id   = 'mainCopyDepth'
                    this.bufferPlane.name = 'mainCopyDepth'
                    this.renderer.clearColor(0,0,0,0)
                    this.bufferPlane.material = this.copyMaterial;
                    this.copyMaterial.setUniform('tInput', this.readGFrameBuffer)
                    this.renderer.clear()
                    this.renderer.render(this.bufferScene, this.bufferCamera, this.lastDepthFrameBuffer);
                    settings.useEditor && debugData.renderPasses.push({name: 'lastDepthRenderPass', buffer: this.lastDepthFrameBuffer })
                // }
                
        }


        // if (!settings.extraPerfBoost) {
            this.renderHDPasses()
        // }

        
        this.numRenderedFrames = this.numRenderedFrames || 0
        this.numRenderedFrames++
        if (this.numRenderedFrames > 3) {
            settings.uClear = false;
            if (this.scenereadyCallback) {
                this.onSceneReady()
            }
        }

        





    }

    onSceneReady() {
        if (this.scenereadyCallback) {
            this.scenereadyCallback()
            this.scenereadyCallback = null
        }
    }

    simpleRender() {
        if (!this.scene) return
        this.updateCameras()
        this.updateUniforms()
        this.renderer.clearColor(0,0,0,1);
        this.renderer.clear() 
        this.renderer.render(this.scene, this.camera, this.writeHDBuffer)
        this.swapHDBuffers();
        this.renderHDPasses()
    }

    renderHDPasses() {

        // /** depth Of Field */
        // if (settings.useDOF && settings.uBokehRadius > 0.01) {
        //     this.dofRenderPass.render( this.bufferScene, this.bufferPlane, this.bufferCamera, this.readHDBuffer, this.writeHDBuffer, this.readGFrameBuffer )  
        //     this.swapHDBuffers();
        // }

        /** Bloom */
        if (settings.useBloom && this.useBloom) {
            this.bloomRenderPass.render( this.bufferScene, this.bufferPlane, this.bufferCamera, this.readHDBuffer, this.writeHDBuffer )  
            settings.useEditor && debugData.renderPasses.push({name: 'BloomRenderPass', buffer: this.writeHDBuffer })
            this.swapHDBuffers();
        }

        /** Tone Mapping */
        //dong tonemapping before last pass help improving sharpen and chromatic abberation effects
        
        if (this.useToneMapping  && (settings.useACESFilmToneMapping || settings.useACESFitted)) {
            this.toneMappingRenderPass.render( this.bufferScene, this.bufferPlane, this.bufferCamera, settings.useTemporalAA?this.readHDBuffer:this.readBuffer, settings.useTemporalAA?this.writeHDBuffer : this.writeBuffer )  
            settings.useEditor && debugData.renderPasses.push({name: 'ToneMappingRenderPass', buffer: settings.useTemporalAA?this.writeHDBuffer : this.writeBuffer })
            this[settings.useTemporalAA?"swapHDBuffers": "swapBuffers"]();
        }
        
        //DRAW DEBUG PASS
        if (settings.useEditor) {
            debugData.renderPasses && debugData.renderPasses.forEach((pass, i)=>{
                if (pass.name == debugData.debugRenderPass) {
                    this.CopyRenderPass.render( this.bufferScene, this.bufferPlane, this.bufferCamera, pass.buffer, this.debugFrameBuffer )
                    this._uniforms.tDebug = this.debugFrameBuffer;
                }
            })
        }


        /** 
         * reflectionProbes & lightProbes 
         **/
        this._uniforms['tReflectionProbe'] = this.reflectionProbeTexture




        /** Final Pass: ToneMap, Sharpen, Vignetting, Chromatic aberation */
        // this.renderer.clearColor(0,0,0,1)
        // this.renderer.clear()
        this.bufferPlane.material = this.finalMaterial
        
        this.finalMaterial.setUniform('tInput', settings.useTemporalAA ?  this.readHDBuffer : this.readBuffer);

        this.finalMaterial.setDefine('DEBUG_SKY', settings.debugSky?1:null)

        this.finalMaterial.setDefine('USE_COLOR_CORRECTION', this.useColorCorrection && settings.useColorCorrection ? 1 : null)
        this.finalMaterial.setDefine('USE_FILM_GRAIN', settings.useFilmGrain ? 1 : null)
        this.finalMaterial.setDefine('USE_CHROMATIC_ABERRATION', this.useChromaticAberration && settings.useChromaticAberration ? 1 : null)
        this.finalMaterial.setDefine('USE_VIGNETTING', settings.useVignetting ? 1 : null)
        this.finalMaterial.setDefine('USE_SHARPEN', settings.useSharpen ? 1 : null)
        this.finalMaterial.setDefine('USE_POST_TONEMAPPING', settings.usePostToneMapping ? 1 : null)
        
        this.finalMaterial.setUniform('uChromaticAberrationOffset',  this.chromaticAberrationOffset);
        this.finalMaterial.setUniform('uVignettingColor',  this.vignettingColor );
        this.finalMaterial.setUniform('uVignettingStrength',  this.vignettingStrength );
        this.finalMaterial.setUniform('uBrightness', settings.uBrightness );
        this.finalMaterial.setUniform('uContrast', settings.uContrast);
        this.finalMaterial.setUniform('uSaturation', settings.uSaturation);
        this.finalMaterial.setUniform('uRed', settings.uRed);
        this.finalMaterial.setUniform('uGreen', settings.uGreen);
        this.finalMaterial.setUniform('uBlue', settings.uBlue);
        this.finalMaterial.setUniform('uACESExpCompensation', settings.uACESExpCompensation);

        this.finalMaterial.name = 'finalMaterial'
        if (this.finalMaterial.uniforms.uBlue) {
            this.finalMaterial.uniforms.uBlue.value = settings.uBlue
        }
        this.finalMaterial.setUniform('uFilmGrainAmount', settings.uFilmGrainAmount);
        this.finalMaterial.setUniform('useToneMapping', this.useToneMapping);

        this.bufferPlane.id = 'FinalPass'
        this.bufferPlane.name = 'FinalPass'
        this.renderer.render(this.bufferScene, this.bufferCamera)
        
        if (settings.useEditor) {
            debugData.renderPasses.push({name: 'finalRenderPass', material: this.finalMaterial })
        }

    }

}